import { useState, useEffect } from "react";
import Button from "../../components/Button";
import Table from "../../components/Table/Table";
import { TableSearch } from "../../components/Table/TableActions";
import Modal from "../../components/Modal";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import Badge from "../../components/Badge";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import useWindowSize from "../../hooks/useWindow";
import CSVExport from "../../components/CsvExport/CsvExport";

const EMPLOYEE_ENDPOINT = "/api/v1/employees/";

const AllEmployees = ({ data }) => {
  const [searchResult, setSearchResult] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [batchReactivateModal, setBatchReactivateModal] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isBatchReactivating, setIsBatchReactivating] = useState(false);
  const [allEmployeesData, setAllEmployeesData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();

  const handleDeleteModal = (row) => {
    setCurrentRow(row);
    setDeleteModal(!deleteModal);
  };

  const handleBatchReactivateModal = () => {
    setBatchReactivateModal(!batchReactivateModal);
  };

  const width = useWindowSize();
  const mobile = width < 600;

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  const handleCloseBatchReactivateModal = () => {
    setBatchReactivateModal(false);
  };

  const handleDeleteEmployee = () => {
    setIsDeleting(true);
    httprequest.delete(`${EMPLOYEE_ENDPOINT}${currentRow?.employee_id}`).then((res) => {
      setIsDeleting(false);
      toast.success(res?.data?.message);
      window.location.reload();
    }).catch((err) => {
      setIsDeleting(false);
      toast.error(err?.response?.data);
    });
  };

  const handleReactivateEmployee = () => {
    setIsReactivating(true);

    httprequest.patch(`${EMPLOYEE_ENDPOINT}${currentRow?.employee_id}/reactivate`)
      .then((res) => {
        setIsReactivating(false);
        toast.success(res?.data?.message);
        window.location.reload();
      })
      .catch((err) => {
        setIsReactivating(false);
        toast.error(err?.response?.data?.message || "Error reactivating employee");
      });
  };

  const handleBatchReactivate = () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one employee to reactivate");

      return;
    }

    const inactiveSelectedEmployees = selectedRows.filter(employeeId => {
      const employee = allEmployeesData.find(emp => emp.employee_id === employeeId);

      return employee && !employee.is_active;
    });

    if (inactiveSelectedEmployees.length === 0) {
      toast.error("No inactive employees selected");

      return;
    }

    setIsBatchReactivating(true);

    const reactivateRequests = inactiveSelectedEmployees.map(employeeId => httprequest.patch(`${EMPLOYEE_ENDPOINT}${employeeId}/reactivate`)
    );

    Promise.all(reactivateRequests)
      .then((responses) => {
        setIsBatchReactivating(false);
        const successCount = responses.filter(response => response.status === 200).length;
        toast.success(`Successfully reactivated ${successCount} employee(s)`);
        setBatchReactivateModal(false);
        window.location.reload();
      })
      .catch(() => {
        setIsBatchReactivating(false);
        toast.error("Error reactivating some employees");
      });
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Name</p>}
          {`${row?.first_name} ${row?.last_name}` || "--/--"}
        </>
      )
    },
    {
      name: "Email Address",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Email Address</p>}
          {row?.email || "--/--"}
        </>
      )
    },
    {
      name: "Role",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Role</p>}
          {row?.role?.name || "--/--"}
        </>
      )
    },
    {
      name: "Status",
      cell: (row) => (<Badge status={row?.is_active} />)
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row.employee_id} className="table-action-container">
          <button
            onClick={() => navigate(`/users/employees/${row?.employee_id}`)}
            className="table-action-icon"
          >
            <Icon icon="fluent:eye-16-regular" />
          </button>
          <button
            onClick={() => navigate(`/users/employees/${row?.employee_id}`)}
            className="table-action-icon"
          >
            <Icon icon="heroicons:pencil-square-solid" />
          </button>
          <button
            onClick={() => handleDeleteModal(row)}
            className="table-action-icon"
            key={row.employee_id}
          >
            {row?.is_active === true ?
              <div className="delete-icon"><Icon icon="mingcute:delete-2-line" /></div>
              : <div className="approve-icon"><Icon icon="mdi:checkbox-marked-circle-outline" /></div>
            }
          </button>
          <button onClick={() => navigate(`/employees/password/${row?.employee_id}`)} className="table-action-icon">
            <Icon icon="material-symbols:key-outline-rounded" />
          </button>
        </div>
      )
    }
  ];

  const handleSearchTeacher = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");

    const result = allEmployeesData?.filter((data) => valArray?.every(
      (word) => data?.first_name
        ?.toLowerCase()
        .includes(word.toLowerCase()) ||
        data?.last_name
          ?.toLowerCase()
          .includes(word.toLowerCase()) ||
        data?.email
          ?.toLowerCase()
          .includes(word.toLowerCase())
    ));
    setSearchResult(result);
  };

  useEffect(() => {
    setAllEmployeesData(data);
    setSearchResult(data);
  }, [data]);

  // Count inactive employees in selected rows
  const selectedInactiveCount = selectedRows.filter(employeeId => {
    const employee = allEmployeesData.find(emp => emp.employee_id === employeeId);

    return employee && !employee.is_active;
  }).length;

  return (
    <>
      <Table
        data={searchResult}
        columns={columns}
        keyField="employee_id"
        handleRowSelected={({ selectedRows }) => setSelectedRows(selectedRows.map(row => row.employee_id))}
        subHeader={true}
        pagination
        subHeaderComponent={
          data?.length > 0 && (
            <div className="action_div">
              <TableSearch placeholder="Search here..." searchTableFunc={handleSearchTeacher} />

              <CSVExport
                data={data}
                filename="employees_export"
                idKey="employee_id"
                selectedRows={selectedRows}
                fields={[
                  { path: "email", header: "Email Address" },
                  { path: "full_name", header: "Full Name" },
                  { path: "phone_number", header: "Phone Number" },
                  { path: "gender", header: "Gender" },
                  { path: "role.name", header: "Role" },
                  { path: "is_active", header: "Active Status" }
                ]}
                onBatchReactivate={handleBatchReactivateModal}
                isBatchReactivating={isBatchReactivating}
              />
            </div>
          )
        }
      />

      {/* Single Employee Action Modal */}
      <Modal
        title={currentRow?.is_active === true ? "Off board employee" : "Re-activate Employee"}
        subtitle={
          currentRow?.is_active === true
            ? (
              <div>
                Are you sure you want to off board
                <span className="modal-name-highlight"> {`${currentRow?.last_name} ${currentRow?.first_name}`}</span>?
                If deleted, the employee will no longer have access to this platform.
              </div>
            )
            : (
              <div>
                Are you sure you want to re-activate
                <span className="modal-name-highlight"> {`${currentRow?.last_name} ${currentRow?.first_name}`}</span>?
                If re-activated, the employee will have access to this platform.
              </div>
            )
        }
        modalContent={<></>}
        modalFooter={
          <>
            {currentRow?.is_active === true ?
              <Button variant="danger" onClick={handleDeleteEmployee} isLoading={isDeleting} buttonName="Off board Employee" buttonSize="full" color="btndefault" />
              : <Button variant="primary" isLoading={isReactivating} onClick={handleReactivateEmployee} buttonName="Re-activate Employee" buttonSize="full" color="btndefault" />
            }
          </>
        }
        isopen={deleteModal}
        closeModal={handleCloseDeleteModal}
      />

      {/* Batch Reactivate Modal */}
      <Modal
        title="Batch Reactivate Employees"
        subtitle={
          <div>
            Are you sure you want to reactivate <span className="modal-name-highlight">{selectedInactiveCount}</span> employee(s)?
            If re-activated, these employees will have access to this platform.
          </div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <Button
              variant="primary"
              isLoading={isBatchReactivating}
              onClick={handleBatchReactivate}
              buttonName={`Reactivate ${selectedInactiveCount} Employee(s)`}
              buttonSize="full"
              color="btndefault"
            />
          </>
        }
        isopen={batchReactivateModal}
        closeModal={handleCloseBatchReactivateModal}
      />
    </>
  );
};

export default AllEmployees;
